import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Alert, Button, Result, Spin } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { API, TOKEN_LS_PATH } from '../api';
import { PageTitle } from '../components/common/page-title';
import {
  TPlayerIndexes,
  VideoPlayerIndexesBar,
  normalizeIndexValue,
} from '../components/video-player-indexes-bar';
import { PLAYLIST_ID_PARAM_NAME } from '../hooks/use-playlist-path-id';
import { Paths } from '../types/common';
import { TPlayListItem, TPlaylistContentItem } from '../types/responses/video';
import {
  getPlayerLink,
  getStudentPlaylistItemPath,
  getStudentPlaylistPath,
} from '../utils/paths';
import { isVideoPlaylistItem } from '../utils/playlist-item-kind';
import { getAiManagerModule1DisplayTitle } from './aimanager-module1-display-titles';

const Toolbar = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;

const ToolbarCenter = styled('div')`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

const MaterialTitle = styled(PageTitle)`
  min-width: 0;
  margin: 0;
  text-align: center;
`;

const Viewer = styled('iframe')`
  display: block;
  width: 100%;
  height: calc(100vh - 190px);
  min-height: 560px;
  border: 1px solid ${({ theme }) => theme.COLORS.BLACK._500};
  border-radius: 8px;
  background: ${({ theme }) => theme.COLORS.BLACK._400};
`;

const NextButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$ready',
})<{ $ready?: boolean }>`
  ${({ $ready }) =>
    $ready
      ? `
        background: #23c55e;
        border-color: #23c55e;
        color: #ffffff;

        &:hover,
        &:focus {
          background: #16a34a !important;
          border-color: #16a34a !important;
          color: #ffffff !important;
        }
      `
      : ''}
`;

const getPlaylistItemDisplayName = (item: TPlaylistContentItem) =>
  getAiManagerModule1DisplayTitle(item.s3Key, item.name);

const isRunningInsideFrame = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const markPlaylistItemComplete = (
  playlist: TPlayListItem,
  completedIndex: number
): TPlayListItem => ({
  ...playlist,
  items: playlist.items.map((playlistItem, index) => {
    if (index === completedIndex) {
      return { ...playlistItem, completed: true };
    }

    if (index === completedIndex + 1) {
      return { ...playlistItem, locked: false };
    }

    return playlistItem;
  }),
});

export const StudentPlaylistItemPage: FC = () => {
  const navigate = useNavigate();
  const params = useParams<{
    [PLAYLIST_ID_PARAM_NAME]: string;
    itemIndex: string;
  }>();
  const playlistId = params[PLAYLIST_ID_PARAM_NAME];
  const itemIndex = Number(params.itemIndex);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [item, setItem] = useState<TPlaylistContentItem>();
  const [playlist, setPlaylist] = useState<TPlayListItem>();
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [playerIndexes, setPlayerIndexes] = useState<TPlayerIndexes | null>(
    null
  );
  const isFramed = isRunningInsideFrame();
  const optimisticCompletedIndexesRef = useRef(new Set<number>());

  const applyOptimisticCompletions = useCallback((data: TPlayListItem) => {
    let nextData = data;
    optimisticCompletedIndexesRef.current.forEach((completedIndex) => {
      nextData = markPlaylistItemComplete(nextData, completedIndex);
    });

    return nextData;
  }, []);

  const loadProgress = useCallback(async () => {
    if (!playlistId || !Number.isInteger(itemIndex)) return;
    const response = await API.playlists.getById(playlistId);
    const nextPlaylist = applyOptimisticCompletions(response.data);
    setPlaylist(nextPlaylist);
    setItem(nextPlaylist.items[itemIndex]);
  }, [applyOptimisticCompletions, itemIndex, playlistId]);

  useEffect(() => {
    if (!playlistId || !Number.isInteger(itemIndex)) {
      setDenied(true);
      setLoading(false);
      return;
    }
    Promise.all([API.playlists.getItem(playlistId, itemIndex), loadProgress()])
      .then(([response]) => setItem(response.data))
      .catch(() => setDenied(true))
      .finally(() => setLoading(false));
  }, [itemIndex, loadProgress, playlistId]);

  useEffect(() => {
    const timer = window.setInterval(() => void loadProgress(), 5000);
    return () => window.clearInterval(timer);
  }, [loadProgress]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) {
        return;
      }

      if (event.data?.type === 'plai:qualified-complete') {
        optimisticCompletedIndexesRef.current.add(itemIndex);
        setItem((current) =>
          current ? { ...current, completed: true } : current
        );
        setPlaylist((current) =>
          current ? markPlaylistItemComplete(current, itemIndex) : current
        );
        void loadProgress();
        window.setTimeout(() => void loadProgress(), 1500);
      }

      if (event.data?.type === 'plai:indexes-update') {
        const indexes = event.data.indexes ?? {};
        setPlayerIndexes({
          amazement: normalizeIndexValue(indexes.amazement),
          attention: normalizeIndexValue(indexes.attention),
          happiness: normalizeIndexValue(indexes.happiness),
          involvement: normalizeIndexValue(indexes.involvement),
          tiredness: normalizeIndexValue(indexes.tiredness),
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [itemIndex, loadProgress]);

  const frameUrl = useMemo(() => {
    if (!item) return '';
    if (isVideoPlaylistItem(item)) {
      const playerUrl = new URL(getPlayerLink(item.linkId));
      playerUrl.searchParams.set('courseEmbed', '1');
      return playerUrl.toString();
    }
    return `${Paths.MODULE1_VIEW}?assetLinkId=${encodeURIComponent(
      item.linkId
    )}&from=${encodeURIComponent(
      getStudentPlaylistPath(playlistId ?? '')
    )}&embedded=1`;
  }, [item, playlistId]);
  const isCurrentItemVideo = item ? isVideoPlaylistItem(item) : false;

  useEffect(() => {
    setPlayerIndexes(null);
  }, [frameUrl]);

  const postStudentAuthToPlayer = useCallback(() => {
    if (!isCurrentItemVideo || !frameUrl) return;

    const token = localStorage.getItem(TOKEN_LS_PATH);
    if (!token) return;

    frameRef.current?.contentWindow?.postMessage(
      {
        type: 'plai:student-auth',
        token,
      },
      new URL(frameUrl).origin
    );
  }, [frameUrl, isCurrentItemVideo]);

  useEffect(() => {
    if (!isCurrentItemVideo || !frameUrl) return;

    postStudentAuthToPlayer();
    const interval = window.setInterval(postStudentAuthToPlayer, 2000);

    return () => window.clearInterval(interval);
  }, [frameUrl, isCurrentItemVideo, postStudentAuthToPlayer]);

  if (isFramed) {
    return (
      <Result
        status="403"
        title="Nested playlist view is blocked"
        subTitle="Playlist materials must open only in the top-level player."
      />
    );
  }

  if (loading) return <Spin />;
  if (denied || !item || !playlistId) {
    return (
      <Result
        status="403"
        title="This material is locked"
        subTitle="Complete the previous playlist material first."
        extra={
          <Button onClick={() => navigate('/playlists')}>
            Back to playlists
          </Button>
        }
      />
    );
  }

  const nextItem = playlist?.items[itemIndex + 1];
  const canOpenNext = Boolean(item.completed && nextItem && !nextItem.locked);
  const itemDisplayName = getPlaylistItemDisplayName(item);

  return (
    <div>
      <Toolbar>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(getStudentPlaylistPath(playlistId))}
        >
          Playlist
        </Button>
        <ToolbarCenter>
          {!isCurrentItemVideo && (
            <MaterialTitle>
              {item.order ?? itemIndex + 1}. {itemDisplayName}
            </MaterialTitle>
          )}
          {isCurrentItemVideo && (
            <VideoPlayerIndexesBar
              indexes={playerIndexes}
              title={`${item.order ?? itemIndex + 1}. ${itemDisplayName}`}
            />
          )}
        </ToolbarCenter>
        <NextButton
          type="primary"
          icon={<ArrowRightOutlined />}
          disabled={!canOpenNext}
          $ready={canOpenNext}
          onClick={() =>
            navigate(getStudentPlaylistItemPath(playlistId, itemIndex + 1))
          }
        >
          Next
        </NextButton>
      </Toolbar>
      {item.completed && (
        <Alert
          type="success"
          showIcon
          message="Qualified study completed"
          style={{ marginBottom: 16 }}
        />
      )}
      <Viewer
        key={frameUrl}
        ref={frameRef}
        title={itemDisplayName}
        src={frameUrl}
        allow="camera; microphone; autoplay; fullscreen"
        onLoad={postStudentAuthToPlayer}
      />
    </div>
  );
};
